<template>
  <div class="bulk-buy-component">
    <p class="text-secondary bulk-buy-intro mb-4">
      {{ $t("bulk_buy.intro") }}
    </p>

    <!-- Top zone: optional accelerator (left) + sourcing preference (right) -->
    <div class="row g-3 mb-4">
      <div class="col-lg-7">
        <BulkBuyUploadSection
          :companyId="companyId"
          @analysis-complete="handleAIAnalysis"
        />
      </div>
      <div class="col-lg-5">
        <SourcingPreferenceCard
          v-model="globalSettings"
          @user-select="userSelectedSourcingMode = true"
        />
      </div>
    </div>

    <!-- Custom sourcing requirement (Other mode) — full-width card below the top row -->
    <CustomRequirementCard
      v-if="isOtherMode"
      v-model="globalSettings.custom_requirement"
    />

    <!-- Beneficiaries -->
    <div class="d-flex justify-content-between align-items-end mb-3">
      <div>
        <h4 class="font-serif mb-1">
          {{ $t("bulk_buy.destination_companies") }}
        </h4>
        <p class="text-secondary small mb-0">
          {{ $t("bulk_buy.destination_companies_description") }}
        </p>
      </div>
      <button class="btn btn-primary flex-shrink-0" @click="addBeneficiary">
        <FontAwesomeIcon icon="plus" class="me-1" />
        {{ $t("bulk_buy.add_beneficiary") }}
      </button>
    </div>

    <div class="card beneficiaries-card mb-4">
      <!-- Populated: beneficiary table -->
      <div v-if="companyRows.length > 0" class="table-responsive">
        <DataTable
          :value="companyRows"
          dataKey="temp_id"
          class="p-datatable-sm"
          :rowClass="getRowClass"
        >
          <Column field="country" headerStyle="width: 32%;">
            <template #header>
              <InfoTooltip :text="$t('bulk_buy.tooltip_country')">
                {{ $t("bulk_buy.country") }}
              </InfoTooltip>
            </template>
            <template #body="{ data, index }">
              <SearchableSelectComponent
                v-model="data.country"
                :options="countryOptions"
                size="sm"
                autoOpen
                clearOnFocus
                :placeholder="$t('common.select')"
                :invalid="isFieldInvalid(data, 'country')"
                @update:modelValue="handleCountryChange(data, index)"
                @blur="data.touched = true"
              />
            </template>
          </Column>

          <Column field="eac_type" headerStyle="width: 18%;">
            <template #header>
              <InfoTooltip :text="$t('bulk_buy.tooltip_eac_type')">
                {{ $t("bulk_buy.eac_type") }}
              </InfoTooltip>
            </template>
            <template #body="{ data }">
              <span
                v-if="derivedEacTypeLabel(data.country)"
                class="text-secondary"
              >
                {{ derivedEacTypeLabel(data.country) }}
              </span>
              <select
                v-else-if="data.country"
                v-model="data.eac_type"
                class="form-select form-select-sm"
              >
                <option value="">{{ $t("common.select") }}</option>
                <option
                  v-for="eacType in formOptions?.selectable_eac_types ?? []"
                  :key="eacType.value"
                  :value="eacType.value"
                >
                  {{ eacType.label }}
                </option>
              </select>
              <span v-else class="text-secondary">—</span>
            </template>
          </Column>

          <Column
            field="volume"
            headerStyle="width: 20%;"
            headerClass="header-align-end"
          >
            <template #header>
              <InfoTooltip :text="$t('bulk_buy.tooltip_volume')">
                {{ $t("bulk_buy.volume") }}
              </InfoTooltip>
            </template>
            <template #body="{ data, index }">
              <input
                v-model.number="data.volume"
                type="number"
                min="1"
                class="form-control form-control-sm text-end"
                :class="{ 'is-invalid': isFieldInvalid(data, 'volume') }"
                @blur="
                  data.touched = true;
                  clearPricingState(index);
                "
              />
            </template>
          </Column>

          <Column
            field="price"
            headerStyle="width: 27%;"
            headerClass="header-align-end"
          >
            <template #header>
              <InfoTooltip
                :text="$t('bulk_buy.tooltip_price')"
                :showIcon="false"
              >
                {{ $t("bulk_buy.price") }}
              </InfoTooltip>
            </template>
            <template #body="{ data, index }">
              <div class="d-flex justify-content-end align-items-center">
                <!-- Other mode: whole order is quoted by hand -->
                <QuotePill v-if="isOtherMode" kind="by-quote" />
                <!-- Idle: per-row quote once the row is complete -->
                <template v-else-if="data.pricing_state.status === 'idle'">
                  <button
                    class="btn btn-sm btn-outline-primary text-nowrap"
                    :disabled="!isBuyRowComplete(data)"
                    @click="fetchPricing(index)"
                  >
                    {{ $t("bulk_buy.ask_for_quote") }}
                  </button>
                </template>
                <!-- No liquid market: priced by hand -->
                <QuotePill
                  v-else-if="data.pricing_state.status === 'agent_reach_out'"
                  kind="manual-quote"
                />
                <!-- loading / success / error -->
                <PricingCell v-else :pricing-state="data.pricing_state" />
              </div>
            </template>
          </Column>

          <Column headerStyle="width: 3%;">
            <template #body="{ index }">
              <button
                class="btn btn-link btn-sm text-danger p-0"
                :aria-label="
                  $t('bulk_buy.remove_beneficiary_row_aria', {
                    index: index + 1,
                  })
                "
                @click="removeBeneficiary(index)"
              >
                <FontAwesomeIcon icon="times" />
              </button>
            </template>
          </Column>
        </DataTable>
      </div>

      <!-- Empty state -->
      <div v-else class="empty-state text-center px-4 py-5">
        <span class="empty-state__icon mb-2">
          <FontAwesomeIcon icon="building" />
        </span>
        <div class="font-serif empty-state__title">
          {{ $t("bulk_buy.no_beneficiaries_title") }}
        </div>
        <div class="text-secondary small empty-state__description">
          {{ $t("bulk_buy.no_beneficiaries_description") }}
        </div>
        <button class="btn btn-primary mt-3" @click="addBeneficiary">
          <FontAwesomeIcon icon="plus" class="me-1" />
          {{ $t("bulk_buy.add_beneficiary") }}
        </button>
      </div>

      <!-- Add another beneficiary / delete all -->
      <div
        v-if="companyRows.length > 0"
        class="border-top add-another-row d-flex justify-content-between align-items-center"
      >
        <button class="btn-ghost btn-add-another" @click="addBeneficiary">
          <FontAwesomeIcon icon="plus" class="me-1" />
          {{ $t("bulk_buy.add_another_beneficiary") }}
        </button>
        <button class="btn-ghost btn-delete-all" @click="deleteAllRows">
          <FontAwesomeIcon icon="trash" class="me-1" />
          {{ $t("common.clear_all") }}
        </button>
      </div>

      <!-- Shared cancellation settings -->
      <SharedCancellationSettingsCard v-model="globalCancellationDefaults" />
    </div>

    <!-- Totals + action bar -->
    <BulkBuySummaryFooter
      :totalVolume="totalVolume"
      :beneficiariesCount="companyRows.length"
      :totalKnownVolume="totalKnownVolume"
      :totalKnownPrice="totalKnownPrice"
      :agentReachOutCount="agentReachOutCount"
      :agentReachOutVolume="agentReachOutVolume"
      :actionState="actionState"
      :canProceed="canProceed"
      @submit="handleMainButtonClick"
    />

    <!-- Confirmation Modal -->
    <BulkBuyConfirmationModal
      ref="confirmationModalRef"
      :companies="completeRows"
      :globalSettings="globalSettings"
      :cancellationSettings="globalCancellationDefaults"
      :registrationAuthorities="formOptions?.registration_authorities"
      :eacTypeLabels="formOptions?.eac_type_labels"
      :is-submitting="isSubmitting"
      @confirm="handleSubmit"
      @cancel="handleCancelSubmission"
    />
  </div>
</template>

<script setup lang="ts">
import InfoTooltip from "@/components/common/InfoTooltip.vue";
import SearchableSelectComponent from "@/components/common/SearchableSelectComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { ALL_COUNTRIES } from "@/constants";
import { getFlagEmoji, translateCountryCode } from "@/utils/countries";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import { computed, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { getDefaultCancelPeriodRange } from "../common/cancelStatementHelpers";
import BulkBuyConfirmationModal from "./BulkBuyConfirmationModal.vue";
import BulkBuySummaryFooter from "./BulkBuySummaryFooter.vue";
import BulkBuyUploadSection from "./BulkBuyUploadSection.vue";
import CustomRequirementCard from "./CustomRequirementCard.vue";
import PricingCell from "./PricingCell.vue";
import QuotePill from "./QuotePill.vue";
import SharedCancellationSettingsCard from "./SharedCancellationSettingsCard.vue";
import SourcingPreferenceCard from "./SourcingPreferenceCard.vue";
import type { AiAnalysisPayload } from "./bulkBuyAiMapping";
import { mapAiCompanies, mergeAiGlobalSettings } from "./bulkBuyAiMapping";
import type {
  BulkBuyActionState,
  BulkBuyAndCancelOrderRequest,
  BulkBuyOrderResponse,
  CancellationMode,
  CompanyRow,
  GlobalCancellationSettings,
  GlobalPurchaseSettings,
} from "./bulkBuyTypes";
import {
  BeneficiaryType,
  CodRequirement,
  defaultOperationNotes,
  derivedEacType,
  eacTypeLabel,
  EACSource,
  Ecolabel,
  effectiveEacType,
  isBuyRowComplete,
  PurchaseMode,
  Support,
} from "./bulkBuyTypes";
import { useBulkBuyOptions } from "./useBulkBuyOptions";
import { useBulkBuyPricing } from "./useBulkBuyPricing";
import { useBulkBuyTotals } from "./useBulkBuyTotals";

const props = defineProps<{
  companyId: number;
}>();

const { t, te } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

const companyRows = ref<(CompanyRow & { temp_id: number })[]>([]);
const isSubmitting = ref(false);
const confirmationModalRef = useTemplateRef<
  InstanceType<typeof BulkBuyConfirmationModal>
>("confirmationModalRef");
const nextTempId = ref(1);
const { options: formOptions } = useBulkBuyOptions();
const userSelectedSourcingMode = ref(false);

// Design shows countries as "🇪🇪 Estonia" in a native dropdown; names come from
// the common.country.* translations, sorted by the localized label.
const countryOptions = computed(() =>
  ALL_COUNTRIES.map(({ value }) => ({
    value,
    label:
      `${getFlagEmoji(value)} ${translateCountryCode(value, t, te)}`.trim(),
  })).sort((a, b) => a.label.localeCompare(b.label))
);

const globalSettings = ref<GlobalPurchaseSettings>({
  purchase_mode: PurchaseMode.CHEAPEST,
  eac_source: EACSource.ANY,
  technology: "ANY",
  ecolabel: Ecolabel.NONE,
  support: Support.ANY,
  cod_requirement: CodRequirement.ANY,
  vintage: "",
  hourly_matching: false,
  monthly_matching: false,
});

const defaultPeriodRange = getDefaultCancelPeriodRange();
const globalCancellationDefaults = ref<GlobalCancellationSettings>({
  period_start: defaultPeriodRange.start,
  period_end: defaultPeriodRange.end,
  beneficiary_type: BeneficiaryType.CONSUMER,
  operation_notes: defaultOperationNotes(BeneficiaryType.CONSUMER),
});

const isOtherMode = computed(
  () => globalSettings.value.purchase_mode === PurchaseMode.OTHER
);

const createNewRow = (): CompanyRow & { temp_id: number } => ({
  temp_id: nextTempId.value++,
  country: "",
  eac_type: "",
  volume: 1,
  pricing_state: { status: "idle" },
});

const derivedEacTypeLabel = (country: string): string | undefined => {
  const authority = derivedEacType(
    country,
    formOptions.value?.registration_authorities
  );
  return authority
    ? eacTypeLabel(authority, formOptions.value?.eac_type_labels)
    : undefined;
};

const handleCountryChange = (
  row: CompanyRow & { temp_id: number },
  index: number
) => {
  row.eac_type = "";
  clearPricingState(index);
};

const {
  isLoadingAny,
  fetchPricing,
  clearPricingState,
  fetchAllPricing,
  canSubmitOrder,
} = useBulkBuyPricing(companyRows, globalSettings);

// Any change to the sourcing settings invalidates cached quotes.
watch(
  globalSettings,
  () => {
    companyRows.value.forEach((_, index) => {
      clearPricingState(index);
    });
  },
  { deep: true }
);

const {
  totalVolume,
  totalKnownVolume,
  totalKnownPrice,
  agentReachOutCount,
  agentReachOutVolume,
} = useBulkBuyTotals(() => companyRows.value);

const completeRows = computed(() => companyRows.value.filter(isBuyRowComplete));

// Every row filled in — required before quoting or requesting a custom quote.
// In Other mode the free-text requirement itself is also required.
const canProceed = computed(
  () =>
    companyRows.value.length > 0 &&
    !isLoadingAny.value &&
    companyRows.value.every(isBuyRowComplete) &&
    (!isOtherMode.value || !!globalSettings.value.custom_requirement?.trim())
);

// Single source of truth for the footer button and the click dispatch below.
const actionState = computed<BulkBuyActionState>(() => {
  if (companyRows.value.length === 0) return "empty";
  if (isLoadingAny.value) return "loading";
  if (isOtherMode.value) return "custom";
  if (canSubmitOrder.value) return "review";
  return "ask";
});

const addBeneficiary = () => {
  companyRows.value.push(createNewRow());
};

const removeBeneficiary = (index: number) => {
  companyRows.value.splice(index, 1);
};

const deleteAllRows = () => {
  companyRows.value = [];
};

const handleMainButtonClick = () => {
  companyRows.value.forEach((row) => {
    row.touched = true;
  });

  if (actionState.value === "custom" || actionState.value === "review") {
    openConfirmationModal();
  } else if (actionState.value === "ask") {
    fetchAllPricing();
  }
};

const getRowClass = (row: CompanyRow): string =>
  row.touched && !isBuyRowComplete(row) ? "cancel-row-invalid" : "";

const isFieldInvalid = (row: CompanyRow, field: keyof CompanyRow): boolean => {
  if (!row.touched) return false;

  const value = row[field];
  if (field === "volume") {
    return !value || (value as number) < 1;
  }
  return !value;
};

// temp_ids of rows added by the AI. The chat emits the FULL updated draft on every turn, so
// AI rows are replaced each time (appending would duplicate them); hand-added rows survive.
const aiRowTempIds = ref<Set<number>>(new Set());

const handleAIAnalysis = (data: AiAnalysisPayload | null) => {
  if (!data?.companies) {
    return;
  }

  globalSettings.value = mergeAiGlobalSettings(
    globalSettings.value,
    data.global_settings,
    { preserveUserPurchaseMode: userSelectedSourcingMode.value }
  );

  const newRows = mapAiCompanies(data.companies).map((row) => ({
    ...row,
    temp_id: nextTempId.value++,
  }));
  companyRows.value = [
    ...companyRows.value.filter((row) => !aiRowTempIds.value.has(row.temp_id)),
    ...newRows,
  ];
  aiRowTempIds.value = new Set(newRows.map((row) => row.temp_id));
  companyRows.value.forEach((_, index) => {
    clearPricingState(index);
  });
};

const openConfirmationModal = () => {
  confirmationModalRef.value?.show();
};

const handleCancelSubmission = () => {
  confirmationModalRef.value?.hide();
};

const handleSubmit = async (
  cancellationModes: Record<number, CancellationMode>
) => {
  try {
    isSubmitting.value = true;

    const request: BulkBuyAndCancelOrderRequest = {
      orders: completeRows.value.map((row) => ({
        volume: row.volume,
        residency: row.country,
        eac_type: effectiveEacType(
          row,
          formOptions.value?.registration_authorities
        ),
        quoted_base_price:
          row.pricing_state.status === "success"
            ? row.pricing_state.base_price
            : undefined,
        quoted_brokerage_fee:
          row.pricing_state.status === "success"
            ? row.pricing_state.brokerage_fee
            : undefined,
        quoted_premiums:
          row.pricing_state.status === "success"
            ? row.pricing_state.premiums
            : undefined,
        quoted_cancellation_fee:
          row.pricing_state.status === "success"
            ? row.pricing_state.cancellation_fee
            : undefined,
        quoted_minimum_cancellation_fee:
          row.pricing_state.status === "success"
            ? row.pricing_state.minimum_cancellation_fee
            : undefined,
        quoted_total_price:
          row.pricing_state.status === "success"
            ? row.pricing_state.price
            : undefined,
        pricing_request_id:
          row.pricing_state.status === "success" ||
          row.pricing_state.status === "agent_reach_out"
            ? row.pricing_state.pricing_request_id
            : undefined,
        cancellation_mode: cancellationModes[row.temp_id] ?? undefined,
      })),
      global_settings: globalSettings.value,
      cancellation_settings: globalCancellationDefaults.value,
      payer_company_id: props.companyId,
    };

    const response = await goSolidApi.post<BulkBuyOrderResponse>(
      "/api/eac/buy-and-cancel/submit",
      request
    );

    confirmationModalRef.value?.hide();

    if (response.data.total_successful > 0) {
      globalSuccessHandler(
        "Orders Submitted",
        `Successfully submitted ${response.data.total_successful} orders` +
          (response.data.total_failed > 0
            ? ` (${response.data.total_failed} failed)`
            : "")
      );

      companyRows.value = [];
    } else {
      throw new Error("All orders failed to submit");
    }
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped lang="scss">
.bulk-buy-component {
  max-width: 1400px;
}

.bulk-buy-intro {
  max-width: 720px;
}

.beneficiaries-card {
  overflow: hidden;
}

.table-responsive {
  max-height: 600px;
}

// Roomier rows + quiet uppercase eyebrow headers + a faint pink wash on invalid
// rows — mirrors CancelStatementsEditor.
:deep(.pv-datatable-header-cell),
:deep(.pv-datatable-body-cell) {
  white-space: normal;
  overflow: visible;
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
  vertical-align: middle;
}

:deep(.pv-datatable-header-cell) {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--phoenix-secondary-color);
}

// Right-align the numeric column headers with their cell content. The header
// cell wraps its title in a flex row, so push the content to the end — and
// because the title span flex-grows, align its text too for wrapped labels.
:deep(.header-align-end [data-pc-section="columnheadercontent"]) {
  justify-content: flex-end;
  text-align: end;
}

:deep(.pv-datatable-body-row.cancel-row-invalid),
:deep(.pv-datatable-body-row.cancel-row-invalid:hover) {
  background: color-mix(
    in srgb,
    var(--phoenix-danger-bg-subtle) 30%,
    var(--phoenix-emphasis-bg)
  );
}

.add-another-row {
  padding: 0.6rem 1.25rem;
  background: var(--phoenix-body-bg);
}

.btn-add-another {
  border: 0;
  background: transparent;
  padding: 0.25rem 0;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--phoenix-primary);
}

// Quiet destructive counterpart to "Add another beneficiary": muted until
// hovered so it can't be confused for the row-level primary action.
.btn-delete-all {
  border: 0;
  background: transparent;
  padding: 0.25rem 0;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--phoenix-secondary-color);

  &:hover {
    color: var(--phoenix-danger);
  }
}

.empty-state {
  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    border-radius: 50%;
    background: var(--phoenix-body-highlight-bg);
    color: var(--phoenix-secondary-color);
  }

  &__title {
    font-size: 1rem;
    font-weight: 500;
    color: var(--phoenix-body-color);
  }

  &__description {
    max-width: 26rem;
    margin: 0 auto;
  }
}
</style>
